import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { Alert, Snackbar } from '@mui/material'

import { useTaskContext, type Task, type TaskType } from '@/utils/tasks/TaskContext'

/**
 * Task types that get a toast.
 *
 * Scans and application drafts already render their own progress in the page
 * that started them, so toasting those would just say the same thing twice.
 * Uploads are the ones that outlive their screen — a website crawl runs for
 * minutes after the user has moved on — so they're the ones that need to speak
 * up on their own.
 */
const TOASTED_TYPES: TaskType[] = ['doc_upload', 'funding_need_doc_upload']

const TERMINAL_STATUSES = ['completed', 'error', 'cleaned_up']

// Anything not terminal is still work in progress - matching TaskContext's own
// isTaskActive. A crawl sits at 'queued' for as long as the queue is backed up,
// which is exactly when the user most needs telling that something is running.
function isActive(status: string): boolean {
  return !TERMINAL_STATUSES.includes(status)
}

const RUNNING_MESSAGE =
  'Reading your website pages in the background — you can keep going'
const DONE_MESSAGE = 'Your website pages are ready'
const FAILED_MESSAGE =
  "We couldn't finish reading your website pages. You can add anything missing to your profile."

function endedAt(task: Task): number {
  return task.endTime ? new Date(task.endTime).getTime() : 0
}

// ── Suppression ──────────────────────────────────────────────
// A screen that is itself showing the upload's progress doesn't need a toast
// repeating it — and PROFILE_GENERATING, which blocks on the crawl, would be
// telling the user "you can keep going" while doing the opposite.

let suppressed = false
const suppressionListeners = new Set<() => void>()

function subscribeToSuppression(onChange: () => void): () => void {
  suppressionListeners.add(onChange)
  return () => suppressionListeners.delete(onChange)
}

function readSuppression(): boolean {
  return suppressed
}

/** Silence upload toasts for as long as `active` is true. */
export function useSuppressTaskToasts(active: boolean): void {
  useEffect(() => {
    if (!active) return
    suppressed = true
    suppressionListeners.forEach((listener) => listener())
    return () => {
      suppressed = false
      suppressionListeners.forEach((listener) => listener())
    }
  }, [active])
}

/**
 * Reports background uploads from wherever the user happens to be.
 *
 * Mounted inside TaskProvider, above the router, so a crawl started in the
 * onboarding wizard still reports its outcome after the user has left the step
 * — or the wizard — that started it.
 */
export default function TaskToaster() {
  const { tasks } = useTaskContext()
  const [seen, setSeen] = useState<string[]>([])
  const isSuppressed = useSyncExternalStore(
    subscribeToSuppression,
    readSuppression,
    readSuppression,
  )

  const watched = useMemo(
    () => Object.values(tasks).filter((t) => TOASTED_TYPES.includes(t.type)),
    [tasks],
  )

  // A finished job has something to say and then shuts up, so it takes
  // precedence over the standing "still working" notice.
  const finished = watched
    .filter((t) => TERMINAL_STATUSES.includes(t.status) && !seen.includes(t.id))
    .sort((a, b) => endedAt(b) - endedAt(a))[0]
  const running = watched.find((t) => isActive(t.status))
  const current = finished ?? running

  const handleClose = useCallback(() => {
    if (!finished) return
    setSeen((prev) => (prev.includes(finished.id) ? prev : [...prev, finished.id]))
  }, [finished])

  if (!current || isSuppressed) return null

  // 'completed' only means the job ran. An upload that saved nothing is a
  // failure to the person who was waiting on it.
  const failed =
    !!finished &&
    (finished.status !== 'completed' || finished.results_length === 0)
  const message = finished
    ? failed
      ? FAILED_MESSAGE
      : DONE_MESSAGE
    : RUNNING_MESSAGE

  return (
    <Snackbar
      open
      // The running notice has no end of its own — it disappears when the job
      // does. Only a finished job auto-hides.
      autoHideDuration={finished ? (failed ? 10000 : 6000) : null}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      // The onboarding wizard's Back/Continue sit centred at the bottom of the
      // card. Cap the width so the toast can't reach them on a wide screen, and
      // lift it clear of the stacked nav on a narrow one.
      sx={{ maxWidth: 420, bottom: { xs: 88, sm: 24 } }}
    >
      <Alert
        onClose={finished ? handleClose : undefined}
        severity={finished ? (failed ? 'warning' : 'success') : 'info'}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {message}
      </Alert>
    </Snackbar>
  )
}
